import {
  LANE_IDS,
  applyAction,
  calculateEventLimit,
  getArmyAttackContribution,
  getArmyDefinitionV3ForInstance,
  getArmyDefinitionForInstance,
  getArmyDeploymentPreview,
  getCardInstance,
  getCardInstanceDefinition,
  getCommanderDefinitionForInstance,
  getGarrisonSnapshot,
  getLegalActions,
  getLaneSupplySnapshot,
  getStateHash,
  getSupplySnapshot,
  getTotalAttack,
  getTotalGuard,
  isSetupGameState,
  MAX_ARMY_CARDS_PER_LANE,
  validateAction,
  type DeployArmyAction,
  type DeployCommanderAction,
  type GameState,
  type LaneId,
  type PlayerId,
  type ReplaceCommanderAction,
} from "@fate-kingdom/engine";

import type {
  LaneSideView,
  LaneView,
  LaneViewId,
  HandCardView,
  CommandDisbandCardView,
  ArmyDeploymentTargetView,
  CommanderPlacementActionView,
  CommanderPlacementTargetView,
  CommandViewModel,
  MatchViewModel,
  PlayerViewId,
  ResourcePoolView,
  SupplyViewModel,
} from "../presentation/game-view-model";

const LANE_LABELS: Record<LaneId, string> = {
  left: "Left",
  center: "Centre",
  right: "Right",
};

const EMPTY_RESOURCES: ResourcePoolView = {
  food: 0,
  production: 0,
  trade: 0,
  science: 0,
  culture: 0,
};

function toSideState(
  status: GameState["lanes"][LaneId]["players"][PlayerId]["status"],
): LaneSideView["state"] {
  if (status === "defeated") return "exhausted";
  return status;
}

function toLaneSideView(
  state: Readonly<GameState>,
  laneId: LaneId,
  playerId: PlayerId,
): LaneSideView {
  const side = state.lanes[laneId].players[playerId];
  const landCards = side.land?.cardInstanceIds.length ?? 0;
  const firstLandCardInstanceId = side.land?.cardInstanceIds[0];
  const landDefinition =
    firstLandCardInstanceId === undefined
      ? undefined
      : getCardInstanceDefinition(state, firstLandCardInstanceId);

  return {
    playerId,
    hasLand: side.land !== null,
    landStage: side.land?.stage,
    landDefinitionId: landDefinition?.id,
    landDevelopmentId: landDefinition?.type === "land" ? landDefinition.developmentId : undefined,
    landTypeId:
      landDefinition?.type === "land" &&
      "landTypeId" in landDefinition &&
      typeof landDefinition.landTypeId === "string"
        ? landDefinition.landTypeId
        : undefined,
    landCardInstanceIds: side.land?.cardInstanceIds ?? [],
    landControl: side.land?.currentControl ?? 0,
    landMaxControl: side.land?.maximumControl ?? 0,
    commanderReady: side.commander?.readiness === "ready",
    commanderPresent: side.commander !== null,
    totalAttack: getTotalAttack(state, playerId, laneId),
    totalGuard: getTotalGuard(state, playerId, laneId),
    cardCount:
      landCards +
      (side.commander === null ? 0 : 1) +
      side.armies.length +
      (side.conspiracyCardInstanceId === null ? 0 : 1) +
      side.attachedCardInstanceIds.length,
    state: toSideState(side.status),
  };
}

function toSupplyView(state: Readonly<GameState>): SupplyViewModel {
  if (
    (state.schemaVersion !== 3 && state.schemaVersion !== 4 && state.schemaVersion !== 5) ||
    state.phase !== "supply"
  ) {
    return {
      status: "none",
      lanes: [],
      unsustainableLaneIds: [],
      kingdomPoolContribution: EMPTY_RESOURCES,
      kingdomPool: state.players[state.activePlayerId].kingdomPool,
      legalDiscardActionCount: 0,
    };
  }

  const snapshot = getSupplySnapshot(state);
  const legalDiscards = getLegalActions(state, state.activePlayerId).filter(
    (action) => action.type === "discard-army-for-upkeep",
  );
  const status =
    snapshot.status.type === "awaiting-upkeep-discard"
      ? "awaiting-discard"
      : snapshot.status.type === "resolved"
        ? "resolved"
        : "none";
  const statusMetadata =
    snapshot.status.type === "not-started"
      ? {}
      : {
          playerId: snapshot.status.playerId,
          turnNumber: snapshot.status.turnNumber,
        };

  return {
    status,
    ...statusMetadata,
    lanes: LANE_IDS.map((laneId) => {
      const lane = snapshot.lanes[laneId];
      return {
        laneId,
        label: LANE_LABELS[laneId],
        active: lane.active,
        ...(lane.landTypeId === null ? {} : { landTypeId: lane.landTypeId }),
        ...(lane.landStage === null ? {} : { landStage: lane.landStage }),
        production: lane.production,
        upkeep: lane.upkeep,
        shortfall: lane.shortfall,
        remainder: lane.remainder,
        sustainable: lane.sustainable,
        armies: lane.armyCardInstanceIds.map((instanceId) => {
          const definition = getArmyDefinitionForInstance(state, instanceId);
          return {
            instanceId,
            definitionId: definition?.id ?? "unknown",
            name: definition?.name ?? "Unresolved Army",
            upkeep: definition?.upkeep ?? EMPTY_RESOURCES,
            legalDiscard: legalDiscards.some(
              (action) =>
                action.type === "discard-army-for-upkeep" &&
                action.laneId === laneId &&
                action.armyCardInstanceId === instanceId,
            ),
          };
        }),
      };
    }),
    unsustainableLaneIds: snapshot.unsustainableLaneIds,
    kingdomPoolContribution: snapshot.kingdomPoolContribution,
    kingdomPool: state.players[state.activePlayerId].kingdomPool,
    legalDiscardActionCount: legalDiscards.length,
  };
}

function toCommandView(state: Readonly<GameState>): CommandViewModel {
  if (
    (state.phase !== "command" &&
      !(state.schemaVersion === 5 && state.phase === "action-window")) ||
    !isSetupGameState(state)
  ) {
    return {
      status: "none",
      lanes: [],
      legalDisbandActionCount: 0,
      legalCommanderActionCount: 0,
      legalArmyDeploymentActionCount: 0,
      legalArmyDeploymentActions: [],
    };
  }

  const legalActions = getLegalActions(state, state.activePlayerId);
  const legalDisbands = legalActions.filter(
    (action) => action.type === "disband-commander" || action.type === "disband-army",
  );
  const legalCommanderActions = legalActions.filter(
    (action) => action.type === "deploy-commander" || action.type === "replace-commander",
  );
  const legalArmyDeploymentActions = legalActions.filter((action) => action.type === "deploy-army");
  const toChoice = (
    instanceId: string,
    type: CommandDisbandCardView["type"],
    armySize?: number,
  ): CommandDisbandCardView => {
    const definition = getCardInstanceDefinition(state, instanceId);
    return {
      instanceId,
      definitionId: definition?.id ?? "unknown",
      name: definition?.name ?? `Unresolved ${type === "army" ? "Army" : "Commander"}`,
      type,
      ...(armySize === undefined ? {} : { armySize }),
    };
  };

  const lanes = LANE_IDS.map((laneId) => {
    const side = state.lanes[laneId].players[state.activePlayerId];
    const commanderAction = legalDisbands.find(
      (action) => action.type === "disband-commander" && action.laneId === laneId,
    );
    const commander =
      commanderAction?.type === "disband-commander"
        ? toChoice(commanderAction.commanderCardInstanceId, "commander")
        : undefined;
    const armies = legalDisbands
      .filter((action) => action.type === "disband-army" && action.laneId === laneId)
      .map((action) => {
        if (action.type !== "disband-army") {
          throw new Error("Filtered Command action unexpectedly changed type.");
        }
        const permanent = side.armies.find(
          (army) => army.cardInstanceId === action.armyCardInstanceId,
        );
        return toChoice(action.armyCardInstanceId, "army", permanent?.armySize);
      });
    const garrison = getGarrisonSnapshot(state, state.activePlayerId, laneId);

    return {
      laneId,
      label: LANE_LABELS[laneId],
      active: state.lanes[laneId].result === "active" && side.status === "active",
      commanderPresent: side.commander !== null,
      ...(commander === undefined ? {} : { commander }),
      armies,
      legalChoices: [...(commander === undefined ? [] : [commander]), ...armies],
      armyCount: garrison.armyCount,
      totalArmySize: garrison.totalArmySize,
      totalAttack: getTotalAttack(state, state.activePlayerId, laneId),
      totalGuard: getTotalGuard(state, state.activePlayerId, laneId),
    };
  });

  return {
    status:
      legalDisbands.length === 0 &&
      legalCommanderActions.length === 0 &&
      legalArmyDeploymentActions.length === 0
        ? "none"
        : "available",
    playerId: state.activePlayerId,
    lanes,
    legalDisbandActionCount: legalDisbands.length,
    legalCommanderActionCount: legalCommanderActions.length,
    legalArmyDeploymentActionCount: legalArmyDeploymentActions.length,
    legalArmyDeploymentActions: legalArmyDeploymentActions.map((action) => ({
      playerId: action.playerId,
      laneId: action.laneId,
      armyCardInstanceId: action.armyCardInstanceId,
    })),
  };
}

function createCommanderPlacementAction(
  actionType: CommanderPlacementActionView,
  playerId: PlayerId,
  laneId: LaneId,
  commanderCardInstanceId: string,
): DeployCommanderAction | ReplaceCommanderAction {
  return actionType === "deploy-commander"
    ? { type: actionType, playerId, laneId, commanderCardInstanceId }
    : { type: actionType, playerId, laneId, commanderCardInstanceId };
}

function numericFailureDetail(
  details: Readonly<Record<string, string | number | boolean | null>>,
  key: string,
): number | undefined {
  const value = details[key];
  return typeof value === "number" ? value : undefined;
}

function missingResourceMessage(
  details: Readonly<Record<string, string | number | boolean | null>>,
): string | undefined {
  const resources = [
    ["Food", "requiredFood", "availableFood"],
    ["Production", "requiredProduction", "availableProduction"],
    ["Trade", "requiredTrade", "availableTrade"],
    ["Science", "requiredScience", "availableScience"],
    ["Culture", "requiredCulture", "availableCulture"],
  ] as const;
  const missing = resources.flatMap(([label, requiredKey, availableKey]) => {
    const required = numericFailureDetail(details, requiredKey);
    const available = numericFailureDetail(details, availableKey);
    if (required === undefined || available === undefined || required <= available) return [];
    return [`${required - available} ${label}`];
  });
  return missing.length > 0 ? `Missing ${missing.join(" / ")}.` : undefined;
}

function commanderRejectionMessage(
  code: string,
  message: string,
  details: Readonly<Record<string, string | number | boolean | null>>,
): string {
  if (code === "COMMANDER_CAPACITY_EXCEEDED") {
    const totalArmySize = numericFailureDetail(details, "totalArmySize");
    const armyCapacity = numericFailureDetail(details, "commanderArmyCapacity");
    if (totalArmySize !== undefined && armyCapacity !== undefined) {
      return `Current Army Size is ${totalArmySize}. This Commander's Capacity is ${armyCapacity}.`;
    }
  }

  if (code === "INSUFFICIENT_KINGDOM_POOL") {
    const missing = missingResourceMessage(details);
    if (missing !== undefined) return missing;
  }

  return message;
}

function armyRejectionMessage(
  code: string,
  message: string,
  details: Readonly<Record<string, string | number | boolean | null>>,
): string {
  if (code === "ARMY_CARD_LIMIT_REACHED") {
    const current = numericFailureDetail(details, "currentArmyCount");
    const maximum = numericFailureDetail(details, "maximumArmyCount");
    if (current !== undefined && maximum !== undefined) {
      return `This lane already has ${current} Army cards; the maximum is ${maximum}.`;
    }
  }

  if (code === "COMMANDER_CAPACITY_EXCEEDED") {
    const current = numericFailureDetail(details, "currentArmySize");
    const incoming = numericFailureDetail(details, "incomingArmySize");
    const after = numericFailureDetail(details, "totalArmySizeAfter");
    const capacity = numericFailureDetail(details, "commanderArmyCapacity");
    if (
      current !== undefined &&
      incoming !== undefined &&
      after !== undefined &&
      capacity !== undefined
    ) {
      return `Current Army Size is ${current}. Adding Size ${incoming} would total ${after}, above Capacity ${capacity}.`;
    }
  }

  if (code === "INSUFFICIENT_KINGDOM_POOL") {
    const missing = missingResourceMessage(details);
    if (missing !== undefined) return missing;
  }

  return message;
}

function supplyLifecycleStatus(state: Readonly<GameState>) {
  return state.schemaVersion === 3 || state.schemaVersion === 4 || state.schemaVersion === 5
    ? state.supplyStatus.type
    : ("legacy" as const);
}

function commanderPlacementTarget(
  state: Readonly<GameState>,
  commanderCardInstanceId: string,
  laneId: LaneId,
  actionType: CommanderPlacementActionView | undefined,
  includePreview: boolean,
  stateHashBefore: string,
): CommanderPlacementTargetView {
  const playerId = state.activePlayerId;
  const side = state.lanes[laneId].players[playerId];
  const resolvedActionType =
    actionType ?? (side.commander === null ? "deploy-commander" : "replace-commander");
  const action = createCommanderPlacementAction(
    resolvedActionType,
    playerId,
    laneId,
    commanderCardInstanceId,
  );
  const currentCommanderCardInstanceId = side.commander?.cardInstanceId;
  const currentCommanderDefinition =
    currentCommanderCardInstanceId === undefined
      ? undefined
      : getCardInstanceDefinition(state, currentCommanderCardInstanceId);
  const garrison = getGarrisonSnapshot(state, playerId, laneId);
  const validation = validateAction(state, action);
  const base: CommanderPlacementTargetView = {
    laneId,
    label: LANE_LABELS[laneId],
    actionType: resolvedActionType,
    legal: validation.valid,
    ...(currentCommanderCardInstanceId === undefined
      ? {}
      : {
          currentCommander: {
            instanceId: currentCommanderCardInstanceId,
            definitionId: currentCommanderDefinition?.id ?? "unknown",
            name: currentCommanderDefinition?.name ?? "Unresolved Commander",
          },
        }),
    armyCount: garrison.armyCount,
    totalArmySize: garrison.totalArmySize,
    eventAuthorizationAlreadyUsed:
      state.players[playerId].actionsUsed.eventAuthorizationLanesUsed.includes(laneId),
    kingdomPoolBefore: state.players[playerId].kingdomPool,
    eventLimitBefore: calculateEventLimit(state, playerId),
    totalAttackBefore: getTotalAttack(state, playerId, laneId),
    armyAttackContributionBefore: getArmyAttackContribution(state, playerId, laneId),
    totalGuardBefore: getTotalGuard(state, playerId, laneId),
    incomingZoneBefore: "hand",
    supplyLifecycleBefore: supplyLifecycleStatus(state),
    randomDrawsBefore: state.random.draws,
    randomValueBefore: state.random.value,
    stateHashBefore,
  };

  if (!validation.valid) {
    return {
      ...base,
      rejectionCode: validation.failure.code,
      rejectionMessage: commanderRejectionMessage(
        validation.failure.code,
        validation.failure.message,
        validation.failure.details,
      ),
    };
  }
  if (!includePreview) return base;

  const preview = applyAction(state, action, {
    rulesVersion: state.rulesVersion,
    cardDataVersion: state.cardDataVersion,
  });
  if (!preview.ok) {
    return {
      ...base,
      legal: false,
      rejectionCode: preview.failure.code,
      rejectionMessage: commanderRejectionMessage(
        preview.failure.code,
        preview.failure.message,
        preview.failure.details,
      ),
    };
  }

  const afterSide = preview.state.lanes[laneId].players[playerId];
  const afterCommander = afterSide.commander;
  const battlePreview: GameState = { ...preview.state, phase: "battle" };
  const attackValidation = validateAction(battlePreview, {
    type: "attack",
    playerId,
    laneId,
  });

  return {
    ...base,
    kingdomPoolAfter: preview.state.players[playerId].kingdomPool,
    eventLimitAfter: calculateEventLimit(preview.state, playerId),
    totalAttackAfter: getTotalAttack(preview.state, playerId, laneId),
    armyAttackContributionAfter: getArmyAttackContribution(preview.state, playerId, laneId),
    totalGuardAfter: getTotalGuard(preview.state, playerId, laneId),
    incomingZoneAfter: "battlefield-commander",
    ...(resolvedActionType === "replace-commander" ? { outgoingZoneAfter: "discard" } : {}),
    ...(afterCommander === null
      ? {}
      : {
          deployedThisTurnAfter: afterCommander.deployedThisTurn,
          mayAttackOnDeploymentTurnAfter: afterCommander.mayAttackOnDeploymentTurn,
        }),
    attackEligibleAfter: attackValidation.valid,
    ...(attackValidation.valid ? {} : { attackValidationCodeAfter: attackValidation.failure.code }),
    supplyLifecycleAfter: supplyLifecycleStatus(preview.state),
    randomDrawsAfter: preview.state.random.draws,
    randomValueAfter: preview.state.random.value,
    stateHashAfter: getStateHash(preview.state),
  };
}

export function toCommanderPlacementPreview(
  state: Readonly<GameState>,
  commanderCardInstanceId: string,
  laneId: LaneId,
  actionType: CommanderPlacementActionView,
): CommanderPlacementTargetView {
  return commanderPlacementTarget(
    state,
    commanderCardInstanceId,
    laneId,
    actionType,
    true,
    getStateHash(state),
  );
}

function armyDeploymentTarget(
  state: Readonly<GameState>,
  armyCardInstanceId: string,
  laneId: LaneId,
  includeTransitionPreview: boolean,
  stateHashBefore: string,
): ArmyDeploymentTargetView {
  const playerId = state.activePlayerId;
  const side = state.lanes[laneId].players[playerId];
  const action: DeployArmyAction = {
    type: "deploy-army",
    playerId,
    laneId,
    armyCardInstanceId,
  };
  const garrison = getGarrisonSnapshot(state, playerId, laneId);
  const laneSupply =
    state.schemaVersion === 3 || state.schemaVersion === 4 || state.schemaVersion === 5
      ? getLaneSupplySnapshot(state, laneId)
      : undefined;
  const enginePreview = getArmyDeploymentPreview(state, action);
  const base: ArmyDeploymentTargetView = {
    laneId,
    label: LANE_LABELS[laneId],
    legal: enginePreview.legal,
    armyCountBefore: garrison.armyCount,
    maximumArmyCount: MAX_ARMY_CARDS_PER_LANE,
    totalArmySizeBefore: garrison.totalArmySize,
    commanderPresent: side.commander !== null,
    ...(side.commander?.armyCapacity === undefined
      ? {}
      : { commanderCapacity: side.commander.armyCapacity }),
    commanderReady: side.commander?.readiness === "ready",
    ...(side.commander === null
      ? {}
      : {
          commanderDeployedThisTurn: side.commander.deployedThisTurn,
          commanderMayAttackOnDeploymentTurn: side.commander.mayAttackOnDeploymentTurn,
        }),
    kingdomPoolBefore: state.players[playerId].kingdomPool,
    laneProduction: laneSupply?.production ?? EMPTY_RESOURCES,
    totalUpkeepBefore: laneSupply?.upkeep ?? EMPTY_RESOURCES,
    totalAttackBefore: getTotalAttack(state, playerId, laneId),
    armyAttackContributionBefore: getArmyAttackContribution(state, playerId, laneId),
    totalGuardBefore: getTotalGuard(state, playerId, laneId),
    incomingZoneBefore: "hand",
    deploymentActionsUsedBefore: state.players[playerId].actionsUsed.deploymentActions,
    supplyLifecycleBefore: supplyLifecycleStatus(state),
    randomDrawsBefore: state.random.draws,
    randomValueBefore: state.random.value,
    stateHashBefore,
  };

  if (!enginePreview.legal) {
    return {
      ...base,
      rejectionCode: enginePreview.failure.code,
      rejectionMessage: armyRejectionMessage(
        enginePreview.failure.code,
        enginePreview.failure.message,
        enginePreview.failure.details,
      ),
    };
  }

  const legalBase: ArmyDeploymentTargetView = {
    ...base,
    armyCountAfter: enginePreview.armyCountAfter,
    maximumArmyCount: enginePreview.maximumArmyCount,
    totalArmySizeAfter: enginePreview.totalArmySizeAfter,
    ...(enginePreview.commanderCapacity === undefined
      ? {}
      : { commanderCapacity: enginePreview.commanderCapacity }),
    laneProduction: enginePreview.laneProduction,
    totalUpkeepAfter: enginePreview.totalUpkeepAfter,
    shortfallAfter: enginePreview.shortfallAfter,
    projectedNextSupplySustainable: enginePreview.projectedSustainable,
  };
  if (!includeTransitionPreview) return legalBase;

  const result = applyAction(state, action, {
    rulesVersion: state.rulesVersion,
    cardDataVersion: state.cardDataVersion,
  });
  if (!result.ok) {
    return {
      ...legalBase,
      legal: false,
      rejectionCode: result.failure.code,
      rejectionMessage: armyRejectionMessage(
        result.failure.code,
        result.failure.message,
        result.failure.details,
      ),
    };
  }

  return {
    ...legalBase,
    kingdomPoolAfter: result.state.players[playerId].kingdomPool,
    totalAttackAfter: getTotalAttack(result.state, playerId, laneId),
    armyAttackContributionAfter: getArmyAttackContribution(result.state, playerId, laneId),
    totalGuardAfter: getTotalGuard(result.state, playerId, laneId),
    incomingZoneAfter: "battlefield-army",
    deploymentActionsUsedAfter: result.state.players[playerId].actionsUsed.deploymentActions,
    supplyLifecycleAfter: supplyLifecycleStatus(result.state),
    randomDrawsAfter: result.state.random.draws,
    randomValueAfter: result.state.random.value,
    stateHashAfter: getStateHash(result.state),
  };
}

export function toArmyDeploymentPreview(
  state: Readonly<GameState>,
  armyCardInstanceId: string,
  laneId: LaneId,
): ArmyDeploymentTargetView {
  return armyDeploymentTarget(state, armyCardInstanceId, laneId, true, getStateHash(state));
}

function toHandCardView(
  state: Readonly<GameState>,
  instanceId: string,
  stateHash: string,
): HandCardView {
  const instance = getCardInstance(state, instanceId);
  const definition = getCardInstanceDefinition(state, instanceId);

  if (instance === undefined || definition === undefined) {
    return {
      instanceId,
      definitionId: "unknown",
      name: "Unresolved fixture card",
      type: "event",
    };
  }

  if (definition.type === "land") {
    return {
      instanceId,
      definitionId: definition.id,
      name: definition.name,
      type: definition.type,
      developmentId: definition.developmentId,
      stages: definition.stages,
    };
  }

  const commanderDefinition =
    state.phase === "command" || (state.schemaVersion === 5 && state.phase === "action-window")
      ? getCommanderDefinitionForInstance(state, instanceId)
      : undefined;
  const armyDefinition =
    state.phase === "command" || (state.schemaVersion === 5 && state.phase === "action-window")
      ? getArmyDefinitionV3ForInstance(state, instanceId)
      : undefined;
  return {
    instanceId,
    definitionId: definition.id,
    name: definition.name,
    type: definition.type,
    ...(commanderDefinition === undefined
      ? {}
      : {
          commander: {
            cost: commanderDefinition.cost,
            attack: commanderDefinition.attack,
            guard: commanderDefinition.guard,
            ...("armyCapacity" in commanderDefinition &&
            typeof commanderDefinition.armyCapacity === "number"
              ? { armyCapacity: commanderDefinition.armyCapacity }
              : {}),
            mandateCount: commanderDefinition.mandates.length,
            targets: LANE_IDS.map((laneId) =>
              commanderPlacementTarget(state, instanceId, laneId, undefined, false, stateHash),
            ),
          },
        }),
    ...(armyDefinition === undefined
      ? {}
      : {
          army: {
            cost: armyDefinition.cost,
            armySize: armyDefinition.armySize,
            attack: armyDefinition.attack,
            guard: armyDefinition.guard,
            upkeep: armyDefinition.upkeep,
            targets: LANE_IDS.map((laneId) =>
              armyDeploymentTarget(state, instanceId, laneId, false, stateHash),
            ),
          },
        }),
  };
}

function toZoneCounts(state: Readonly<GameState>, playerId: PlayerId) {
  const player = state.players[playerId];
  return {
    deck: player.deck.length,
    hand: player.hand.length,
    discard: player.discard.length,
    exhaust: player.exhaust.length,
    eventResolution: player.eventResolution.length,
  };
}

export function toMatchViewModel(state: Readonly<GameState>): MatchViewModel {
  const stateHash = getStateHash(state);
  const lanes: LaneView[] = LANE_IDS.map((laneId) => ({
    laneId: laneId as LaneViewId,
    label: LANE_LABELS[laneId],
    result: state.lanes[laneId].result,
    sides: {
      "player-1": toLaneSideView(state, laneId, "player-1"),
      "player-2": toLaneSideView(state, laneId, "player-2"),
    },
  }));

  const opponentPlayerId: PlayerId = state.activePlayerId === "player-1" ? "player-2" : "player-1";
  const setupView = isSetupGameState(state)
    ? {
        gameplayCatalogHash: state.setup.gameplayCatalogHash,
        startingPlayerId: state.setup.startingPlayerId,
        openingHandAttempts: {
          "player-1": state.setup.openingHands["player-1"].drawAttempts,
          "player-2": state.setup.openingHands["player-2"].drawAttempts,
        },
        firstTurnDrawStatus: state.firstTurnDrawStatus,
      }
    : {};
  const base: MatchViewModel = {
    schemaVersion: state.schemaVersion,
    replayVersion: state.schemaVersion,
    matchId: state.matchId,
    rulesVersion: state.rulesVersion,
    cardDataVersion: state.cardDataVersion,
    turnNumber: state.turnNumber,
    phase: state.phase,
    activePlayerId: state.activePlayerId as PlayerViewId,
    status: state.status.type,
    banners: {
      "player-1": state.players["player-1"].victoryBanners,
      "player-2": state.players["player-2"].victoryBanners,
    },
    eventLimit: calculateEventLimit(state, state.activePlayerId),
    stateHash,
    lanes,
    seed: state.seed,
    randomDraws: state.random.draws,
    randomValue: state.random.value,
    landActionsUsed: state.players[state.activePlayerId].actionsUsed.landActions,
    deploymentActionsUsed: state.players[state.activePlayerId].actionsUsed.deploymentActions,
    supply: toSupplyView(state),
    supplyLifecycleStatus:
      state.schemaVersion === 3 || state.schemaVersion === 4 || state.schemaVersion === 5
        ? state.supplyStatus.type
        : "legacy",
    command: toCommandView(state),
    activeHand:
      state.activePlayerId === "player-1"
        ? state.players["player-1"].hand.map((instanceId) =>
            toHandCardView(state, instanceId, stateHash),
          )
        : [],
    activePlayerZones: toZoneCounts(state, state.activePlayerId),
    opponentZones: toZoneCounts(state, opponentPlayerId),
    ...setupView,
  };

  return state.status.type === "finished"
    ? { ...base, winnerId: state.status.winnerPlayerId }
    : base;
}
